import mainStyle from "./mainStyle.module.scss";

import icon_stop from "assets/images/icons/icn_btn_stop.svg";
import icon_setting from "assets/images/icons/icn_btn_settings.svg";
import icon_setting_disabled from "assets/images/icons/icn_btn_settings_grey.svg";
import icon_start from "assets/images/icons/icn_btn_start.svg";
import icon_search_state from "assets/images/icons/icn_search.svg";
import icon_stopped_state from "assets/images/icons/cn_table_cell_stop_red.svg";
import icon_success_state from "assets/images/icons/icn_success.svg";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { useOutletContext } from "react-router-dom";

import ModalContainer from "components/ModalContainer";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import useAuthScope from "hooks/useAuthScope";
import useTableInstance from "hooks/useTableInstance";

import {
  authStateOption,
  scanningTimeout,
  socketIdTypeOption,
} from "./const";
import discoverColumns from "./discoverColumns";
import {
  getSurveillanceAPIKeyAndParams,
  makeSurveillanceAdoptDevicesPayload,
  makeSurveillanceAuthenticateDevicesPayload,
  makeSurveillanceDiscoveryPayload,
  makeSurveillanceStopDiscoveryPayload,
} from "./requests";
import {
  handleSurveillanceAdoptionResponse,
  handleSurveillanceAuthenticationResponse,
  handleSurveillanceDiscoveryResponse,
  handleSurveillanceExistingDevicesResponse,
  handleSurveillanceInventoryDevicesResponse,
  handleSurveillanceSitesResponse,
  handleSurveillanceUnexpectedResponse,
} from "./response";
import Loading from "./loading";
import ModalAdoption from "./modals/ModalAdoption";
import ModalAuthentication from "./modals/ModalAuthentication";
import ModalDiscoverySettings from "./modals/modalSettings";
import ModalDiscoveryNotice from "../modals/modalDiscoveryNotice";
import Slider from "./components/Slider";

const checkMockup = process.env.REACT_APP_IS_MOCKUP === "true";
const mockBatchResponseInterval = 800;

const getResponseData = (response) => (
  response?.result?.batchData ||
  response?.response?.data?.batchData ||
  response?.result?.data ||
  response?.response?.data ||
  response?.batchData ||
  response?.data ||
  []
);

const getErrorMessage = (response) => (
  response?.error?.message ||
  response?.response?.error?.message ||
  response?.message ||
  "Unexpected response"
);

const normalizeIdentity = (value) => String(value || "").toLowerCase();

const Discover = ({
  sendRequest,
  dataMap,
  setIsEmptyTable,
  searchVal,
  goToThePreviousTab,
  changeDiscoverBatchActionsStatus,
  discoverBatchActionTarget,
  setDiscoverBatchActionTarget,
  fetchTableData,
  existedProxyDevices = [],
}) => {
  const { auth, isAuthReady } = useAuthScope();
  const outletContext = useOutletContext();
  const wsData = outletContext?.wsData || [];
  const scanningAbortRef = useRef(false);
  const timeoutRef = useRef(null);
  const activeDiscoverySocketIdRef = useRef(null);
  const lastWsDataIndexRef = useRef(-1);
  const activeActionRequestRef = useRef(null);
  const actionResultIdsRef = useRef(new Set());
  const lastActionWsDataIndexRef = useRef(-1);
  const columnHelper = createColumnHelper();

  const [sitesSocketId, setSitesSocketId] = useState(null);
  const [existedDevicesSocketId, setExistedDevicesSocketId] = useState(null);
  const [inventorySocketId, setInventorySocketId] = useState(null);
  const [socketId, setSocketId] = useState(null);
  const [socketIdType, setSocketIdType] = useState(null);
  const [sites, setSites] = useState([]);
  const [existedDevices, setExistedDevices] = useState([]);
  const [inventoryDevices, setInventoryDevices] = useState([]);
  const [proxyDevices, setProxyDevices] = useState(cloneDeep(existedProxyDevices));
  const [isInitialized, setIsInitialized] = useState({
    sites: false,
    existedDevices: false,
    inventoryDevices: false,
  });
  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanningDone, setScanningDone] = useState(false);
  const [scanningStopped, setScanningStopped] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [openSlider, setOpenSlider] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [readyDevices, setReadyDevices] = useState([]);
  const [results, setResults] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [authenticationStep, setAuthenticationStep] = useState(0);
  const [authenticationLoading, setAuthenticationLoading] = useState(false);
  const [isAuthenticationComplete, setIsAuthenticationComplete] = useState(false);
  const [adoptionStep, setAdoptionStep] = useState(0);
  const [adoptionLoading, setAdoptionLoading] = useState(false);
  const [isAdoptionComplete, setIsAdoptionComplete] = useState(false);
  const [authRequestSource, setAuthRequestSource] = useState(null);
  const [sliderAuthenticationErrorDeviceId, setSliderAuthenticationErrorDeviceId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [credential, setCredential] = useState({ username: "", password: "" });
  const [selectedSiteId, setSelectedSiteId] = useState("inventory");
  const [selectedProxyDevice, setSelectedProxyDevice] = useState(null);
  const [selectedProxyUuid, setSelectedProxyUuid] = useState("");
  const [hasAcknowledgedDiscoveryNotice, setHasAcknowledgedDiscoveryNotice] = useState(false);
  const [showDiscoverySettings, setShowDiscoverySettings] = useState(false);
  const [hasOpenedInitialSettings, setHasOpenedInitialSettings] = useState(false);

  const initializing = !isInitialized.sites || !isInitialized.existedDevices || !isInitialized.inventoryDevices;
  const isOrgLevel = auth?.scopeLevel === "org";
  const isSiteLevel = auth?.scopeLevel === "site";

  const selectedDevices = useMemo(() => devices.filter((device) => device.checked), [devices]);
  const sliderAuthenticatingDeviceId = useMemo(() => {
    if (authRequestSource !== "slider" || socketIdType !== socketIdTypeOption.auth || isAuthenticationComplete) {
      return null;
    }

    return readyDevices[0]?.deviceId || null;
  }, [authRequestSource, isAuthenticationComplete, readyDevices, socketIdType]);
  const showSliderAuthenticationError = selectedDevice?.deviceId &&
    normalizeIdentity(sliderAuthenticationErrorDeviceId) === normalizeIdentity(selectedDevice.deviceId);
  const filteredDevices = useMemo(() => {
    if (!searchVal) {
      return devices;
    }

    const keyword = searchVal.toLowerCase();

    return devices.filter((device) => [
      device.status?.label,
      device.authStatus,
      device.deviceType?.label,
      device.modelName,
      device.deviceId,
      device.ipAddress,
      device.fwVersion,
    ].some((value) => String(value || "").toLowerCase().includes(keyword)));
  }, [devices, searchVal]);

  const disableCheckbox = (device) => {
    if (device.provisioning || !device.authStatus) {
      return true;
    }

    return Boolean(selectedStatus && selectedStatus.authStatus !== device.authStatus && !device.checked);
  };

  const toggleDeviceSelection = (deviceId) => {
    setDevices((prev) => prev.map((device) => (
      device.deviceId === deviceId
        ? { ...device, checked: !device.checked, prevChecked: device.checked }
        : device
    )));
  };

  const toggleAllDeviceSelection = (checked) => {
    const firstSelectedDevice = devices.find((device) => device.checked);
    if (!firstSelectedDevice) {
      return;
    }

    setDevices((prev) => prev.map((device) => {
      if (device.authStatus !== firstSelectedDevice.authStatus || disableCheckbox(device)) {
        return checked ? device : { ...device, checked: false };
      }

      return { ...device, checked };
    }));
  };

  const tableInstance = useTableInstance({
    tableName: "surveillanceAddDeviceDiscover",
    data: filteredDevices,
    columns: discoverColumns(columnHelper, disableCheckbox, toggleDeviceSelection, devices, toggleAllDeviceSelection),
    getRowId: (row) => row.deviceId,
    enableSorting: false,
    enablePagination: false,
    enableGlobalFilter: false,
  });

  const resetBatchActionState = () => {
    changeDiscoverBatchActionsStatus(null);
    setDiscoverBatchActionTarget(null);
  };

  const startDiscoverySetup = () => {
    setHasAcknowledgedDiscoveryNotice(true);
  };

  const fetchSites = () => {
    const payload = getSurveillanceAPIKeyAndParams(auth, "sites");
    const requestUuid = uuidv4();

    setSitesSocketId(requestUuid);
    sendRequest(payload.apiKey, requestUuid, payload.placeholder, {});
  };

  const fetchExistedDevices = () => {
    const payload = getSurveillanceAPIKeyAndParams(auth, "existedDevices");
    const requestUuid = uuidv4();

    setExistedDevicesSocketId(requestUuid);
    sendRequest(payload.apiKey, requestUuid, payload.placeholder, {
      filter: {
        type: "all",
        group: "surveillance",
      },
    });
  };

  const fetchInventoryDevices = (siteId) => {
    const payload = getSurveillanceAPIKeyAndParams(auth, "inventoryDevices");
    const requestUuid = uuidv4();
    const placeholder = { ...payload.placeholder };

    if (siteId) {
      placeholder.siteId = siteId;
    }

    setInventorySocketId(requestUuid);
    sendRequest(payload.apiKey, requestUuid, placeholder, null);
  };

  const scanDevices = () => {
    if (!selectedProxyDevice) {
      setShowDiscoverySettings(true);
      return;
    }

    const payload = makeSurveillanceDiscoveryPayload({ auth });

    scanningAbortRef.current = false;
    setHasSearched(true);
    setScanning(true);
    setScanningDone(false);
    setScanningStopped(false);
    setErrorMessage("");
    setDevices([]);
    resetBatchActionState();
    activeDiscoverySocketIdRef.current = payload.socketId;
    setSocketId(payload.socketId);
    setSocketIdType(socketIdTypeOption.discovery);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, {
      ...payload.params,
      ...(selectedProxyDevice ? { proxyUuid: selectedProxyDevice.proxyUuid } : {}),
    });
    setShowDiscoverySettings(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const scanTimeout = checkMockup
      ? scanningTimeout + (mockBatchResponseInterval * 20)
      : scanningTimeout;

    timeoutRef.current = setTimeout(() => {
      setScanning(false);
      setScanningDone(true);
      activeDiscoverySocketIdRef.current = null;
    }, scanTimeout);
  };

  const rediscover = () => {
    setSelectedDevice(null);
    setOpenSlider(false);
    setSliderAuthenticationErrorDeviceId(null);
    scanningAbortRef.current = false;
    setScanningStopped(false);
    setScanningDone(false);
    scanDevices();
  };

  const retryScanningDevices = () => {
    setDevices([]);
    setScanningDone(false);
    setScanningStopped(false);
    setModalMode(null);
    setOpenSlider(false);
    setSelectedDevice(null);
    setSliderAuthenticationErrorDeviceId(null);
    setErrorMessage("");
    scanningAbortRef.current = false;

    scanDevices();
  };

  const stopScanningDevices = () => {
    const payload = makeSurveillanceStopDiscoveryPayload({ auth });

    scanningAbortRef.current = true;
    activeDiscoverySocketIdRef.current = null;
    setScanning(false);
    setScanningDone(true);
    setScanningStopped(true);
    setOpenSlider(false);
    setSocketIdType(socketIdTypeOption.stopDiscovery);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const openAuthentication = (targetDevices) => {
    setOpenSlider(false);
    setReadyDevices(cloneDeep(targetDevices));
    setResults([]);
    setAuthenticationStep(0);
    setAuthenticationLoading(false);
    setIsAuthenticationComplete(false);
    setAuthRequestSource("modal");
    setCredential({
      username: targetDevices[0]?.username || "",
      password: targetDevices[0]?.password || "",
    });
    setModalMode("auth");
  };

  const openAdoption = (targetDevices) => {
    setOpenSlider(false);
    setReadyDevices(cloneDeep(targetDevices));
    setResults([]);
    setAdoptionStep(0);
    setAdoptionLoading(false);
    setIsAdoptionComplete(false);
    setCredential({
      username: targetDevices[0]?.username || "",
      password: targetDevices[0]?.password || "",
    });
    setSelectedSiteId(auth.scopeLevel === "site" ? auth.sites?.id || "inventory" : "none");
    setModalMode("adopt");
  };

  const sendAuthenticationRequest = () => {
    const payload = makeSurveillanceAuthenticateDevicesPayload({
      auth,
      selectedDevices: readyDevices,
      username: credential.username,
      password: credential.password,
    });

    activeActionRequestRef.current = { id: payload.socketId, type: socketIdTypeOption.auth };
    actionResultIdsRef.current = new Set();
    setSocketId(payload.socketId);
    setSocketIdType(socketIdTypeOption.auth);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, {
      ...payload.params,
      ...(selectedProxyDevice ? { proxyUuid: selectedProxyDevice.proxyUuid } : {}),
    });
    setAuthenticationLoading(true);
    setIsAuthenticationComplete(false);
    setAuthenticationStep(1);
  };

  const authenticateDeviceFromSlider = (device, username, password) => {
    const targetDevices = [{ ...device }];
    const payload = makeSurveillanceAuthenticateDevicesPayload({
      auth,
      selectedDevices: targetDevices,
      username,
      password,
    });

    setAuthRequestSource("slider");
    setReadyDevices(targetDevices);
    setResults([]);
    setAuthenticationLoading(true);
    setIsAuthenticationComplete(false);
    setSliderAuthenticationErrorDeviceId(null);
    setCredential({ username, password });
    activeActionRequestRef.current = { id: payload.socketId, type: socketIdTypeOption.auth };
    actionResultIdsRef.current = new Set();
    setSocketId(payload.socketId);
    setSocketIdType(socketIdTypeOption.auth);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, {
      ...payload.params,
      ...(selectedProxyDevice ? { proxyUuid: selectedProxyDevice.proxyUuid } : {}),
    });
  };

  const sendAdoptionRequest = () => {
    const payload = makeSurveillanceAdoptDevicesPayload({
      auth,
      selectedDevices: readyDevices,
      selectedSiteId,
      selectedProxyDevice,
      username: credential.username,
      password: credential.password,
    });

    activeActionRequestRef.current = { id: payload.socketId, type: socketIdTypeOption.adopt };
    actionResultIdsRef.current = new Set();
    setSocketId(payload.socketId);
    setSocketIdType(socketIdTypeOption.adopt);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
    setAdoptionLoading(true);
    setIsAdoptionComplete(false);
    setAdoptionStep(1);
  };

  const closeResultModal = () => {
    const readyDeviceIds = new Set(readyDevices.map((device) => normalizeIdentity(device.deviceId)));
    const adoptedIds = new Set(results
      .filter((result) => result.isAdopted === true && readyDeviceIds.has(normalizeIdentity(result.deviceId)))
      .map((result) => normalizeIdentity(result.deviceId)));
    const hasAdoptedDevices = adoptedIds.size > 0;

    setDevices((prev) => prev
      .filter((device) => !adoptedIds.has(normalizeIdentity(device.deviceId)))
      .map((device) => ({ ...device, checked: false })));
    setReadyDevices([]);
    setResults([]);
    setModalMode(null);
    setSliderAuthenticationErrorDeviceId(null);
    setAuthenticationStep(0);
    setAuthenticationLoading(false);
    setIsAuthenticationComplete(false);
    setAdoptionStep(0);
    setAdoptionLoading(false);
    setIsAdoptionComplete(false);
    setSocketId(null);
    setSocketIdType(null);
    resetBatchActionState();

    if (hasAdoptedDevices) {
      fetchTableData();
    }
  };

  const applyAuthenticationResultsToDevices = (authResults) => {
    if (authRequestSource === "slider") {
      const selectedResult = authResults.find((item) => (
        normalizeIdentity(item.deviceId) === normalizeIdentity(selectedDevice?.deviceId)
      ));

      if (selectedResult) {
        setSliderAuthenticationErrorDeviceId(selectedResult.isAuthenticated ? null : selectedResult.deviceId);
      }
    }

    setDevices((prev) => prev.map((device) => {
      const result = authResults.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));

      if (!result) {
        return device;
      }

      return {
        ...device,
        authStatus: result.isAuthenticated ? authStateOption.passed : authStateOption.failed,
        isAuthenticatedByDiscovery: false,
        username: result.isAuthenticated ? credential.username : device.username,
        password: result.isAuthenticated ? credential.password : device.password,
        ...(result.isAuthenticated ? {
          deviceAccount: {
            username: credential.username,
            password: credential.password,
          },
        } : {}),
        checked: false,
      };
    }));

    setSelectedDevice((prev) => {
      const result = authResults.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(prev?.deviceId));

      if (!result) {
        return prev;
      }

      return {
        ...prev,
        authStatus: result.isAuthenticated ? authStateOption.passed : authStateOption.failed,
        isAuthenticatedByDiscovery: false,
        username: result.isAuthenticated ? credential.username : prev.username,
        password: result.isAuthenticated ? credential.password : prev.password,
        ...(result.isAuthenticated ? {
          deviceAccount: {
            username: credential.username,
            password: credential.password,
          },
        } : {}),
        checked: false,
      };
    });
  };

  useEffect(() => {
    setIsEmptyTable(devices.length === 0 && hasSearched && scanningDone);
  }, [devices.length, hasSearched, scanningDone, setIsEmptyTable]);

  useEffect(() => {
    if (!hasAcknowledgedDiscoveryNotice) {
      return;
    }

    if (!isAuthReady || !auth?.scopeLevel) {
      return;
    }

    setShowDiscoverySettings(false);
    setIsInitialized({
      sites: false,
      existedDevices: false,
      inventoryDevices: false,
    });
    setSitesSocketId(null);
    setExistedDevicesSocketId(null);
    setInventorySocketId(null);
    setExistedDevices([]);
    setInventoryDevices([]);
    setProxyDevices(cloneDeep(existedProxyDevices));
    setHasOpenedInitialSettings(false);
    if (auth.scopeLevel === "org") {
      fetchSites();
    } else {
      setSites([{ id: auth.sites?.id, name: auth.sites?.name }].filter((site) => site.id));
      setIsInitialized((prev) => ({ ...prev, sites: true }));
    }
    fetchExistedDevices();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthReady, auth?.scopeLevel, auth?.realm?.id, auth?.orgs?.id, auth?.sites?.id, hasAcknowledgedDiscoveryNotice]);

  useEffect(() => {
    if (existedProxyDevices.length === 0) {
      return;
    }

    setProxyDevices(cloneDeep(existedProxyDevices));
  }, [existedProxyDevices]);

  useEffect(() => {
    if (!selectedProxyUuid || proxyDevices.length === 0) {
      return;
    }

    const proxyDevice = proxyDevices.find((device) => device.proxyUuid === selectedProxyUuid);
    if (proxyDevice) {
      setSelectedProxyDevice(proxyDevice);
    }
  }, [proxyDevices, selectedProxyUuid]);

  useEffect(() => {
    if (!hasAcknowledgedDiscoveryNotice || initializing || hasOpenedInitialSettings || selectedProxyDevice) {
      return;
    }

    setShowDiscoverySettings(true);
    setHasOpenedInitialSettings(true);
  }, [hasAcknowledgedDiscoveryNotice, hasOpenedInitialSettings, initializing, selectedProxyDevice]);

  useEffect(() => {
    setSelectedStatus(selectedDevices.length > 0 ? selectedDevices[0] : null);
  }, [selectedDevices]);

  useEffect(() => {
    if (selectedDevices.length === 0) {
      resetBatchActionState();
      return;
    }

    const allFailed = selectedDevices.every((device) => device.authStatus !== authStateOption.passed);
    const allPassed = selectedDevices.every((device) => device.authStatus === authStateOption.passed);

    changeDiscoverBatchActionsStatus({
      hasFailedAuth: allFailed,
      hasMissingGatewayOrDNS: false,
      auth: allFailed,
      adopt: allPassed,
      ipSetting: false,
    });
  }, [selectedDevices.length, selectedDevices.map((device) => device.deviceId).join(","), selectedDevices.map((device) => device.authStatus).join(",")]);

  useEffect(() => {
    if (discoverBatchActionTarget === "auth" && selectedDevices.length > 0) {
      openAuthentication(selectedDevices);
      setDiscoverBatchActionTarget(null);
    }

    if (discoverBatchActionTarget === "adopt" && selectedDevices.length > 0) {
      openAdoption(selectedDevices);
      setDiscoverBatchActionTarget(null);
    }
  }, [discoverBatchActionTarget, selectedDevices]);

  useEffect(() => {
    if (!sitesSocketId || !dataMap[sitesSocketId]) {
      return;
    }

    const updatedSites = handleSurveillanceSitesResponse(dataMap[sitesSocketId]);
    setSites(updatedSites);
    setIsInitialized((prev) => ({ ...prev, sites: true }));
  }, [dataMap, sitesSocketId]);

  useEffect(() => {
    if (!existedDevicesSocketId || !dataMap[existedDevicesSocketId]) {
      return;
    }

    const data = getResponseData(dataMap[existedDevicesSocketId]);
    const updatedExistedDevices = [];
    const updatedInventoryDevices = [];
    const updatedProxyDevices = [];

    data.forEach((item) => {
      const info = item.info || item;
      const productCategory = info.productCategory || info.deviceType || "";

      if (String(productCategory).toLowerCase() === "proxy") {
        updatedProxyDevices.push({
          productCategory,
          deviceName: info.deviceName || info.modelName || "Proxy",
          linkStatus: info.linkStatus || "online",
          proxyUuid: info.id || info.proxyUuid || info.deviceId || info.macAddress || "",
          ipAddress: info.localIpAddr || info.ipAddress || "",
        });
        return;
      }

      const device = {
        deviceId: info.deviceId || info.macAddress || item.deviceId || item.macAddress || "",
      };

      if (!device.deviceId) {
        return;
      }

      if (isOrgLevel && !info.siteName) {
        updatedInventoryDevices.push(device);
        return;
      }

      updatedExistedDevices.push(device);
    });

    setExistedDevices(updatedExistedDevices.length > 0
      ? updatedExistedDevices
      : handleSurveillanceExistingDevicesResponse({ data }));
    setIsInitialized((prev) => ({ ...prev, existedDevices: true }));

    if (isOrgLevel) {
      setInventoryDevices(updatedInventoryDevices);
      setIsInitialized((prev) => ({ ...prev, inventoryDevices: true }));
    }

    if (isSiteLevel && !inventorySocketId) {
      fetchInventoryDevices(auth.sites?.id);
    }

  }, [dataMap, existedDevicesSocketId]);

  useEffect(() => {
    if (!inventorySocketId || !dataMap[inventorySocketId]) {
      return;
    }

    const data = getResponseData(dataMap[inventorySocketId]);
    setInventoryDevices(handleSurveillanceInventoryDevicesResponse({ data }));
    setIsInitialized((prev) => ({ ...prev, inventoryDevices: true }));
  }, [dataMap, inventorySocketId]);

  useEffect(() => {
    if (checkMockup || !activeDiscoverySocketIdRef.current) {
      return;
    }

    const responses = Array.isArray(wsData) ? wsData : [wsData];
    if (lastWsDataIndexRef.current >= responses.length) {
      lastWsDataIndexRef.current = -1;
    }

    for (let i = lastWsDataIndexRef.current + 1; i < responses.length; i += 1) {
      const response = responses[i];
      lastWsDataIndexRef.current = i;

      if ((response?.uuid || response?.id) !== activeDiscoverySocketIdRef.current) {
        continue;
      }

      if (response?.error || response?.response?.error) {
        setScanning(false);
        setScanningDone(true);
        setErrorMessage(getErrorMessage(response));
        setModalMode("error");
        activeDiscoverySocketIdRef.current = null;
        return;
      }

      const data = getResponseData(response);
      if (data.length === 0) {
        setScanning(false);
        setScanningDone(true);
        activeDiscoverySocketIdRef.current = null;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        return;
      }

      setDevices((prev) => {
        const updatedDevices = handleSurveillanceDiscoveryResponse({
          data,
          devices: prev,
          existedDevices,
          inventoryDevices,
        });

        return [...prev, ...updatedDevices];
      });
    }
  }, [wsData]);

  useEffect(() => {
    const activeRequest = activeActionRequestRef.current;
    if (checkMockup || !activeRequest) {
      return;
    }

    const responses = Array.isArray(wsData) ? wsData : [wsData];
    if (lastActionWsDataIndexRef.current >= responses.length) {
      lastActionWsDataIndexRef.current = -1;
    }

    for (let i = lastActionWsDataIndexRef.current + 1; i < responses.length; i += 1) {
      const response = responses[i];
      lastActionWsDataIndexRef.current = i;

      if ((response?.uuid || response?.id) !== activeRequest.id) {
        continue;
      }

      const data = getResponseData(response);
      const isLastResponse = data.length === 0 || response?.error || response?.response?.error;
      const readyDeviceIds = new Set(readyDevices.map((device) => normalizeIdentity(device.deviceId)));

      if (activeRequest.type === socketIdTypeOption.auth) {
        const { updatedReadyDevices, updatedResults } = handleSurveillanceAuthenticationResponse({
          data,
          readyDevices,
          username: credential.username,
          password: credential.password,
        });
        const matchedResults = updatedResults.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId)));

        matchedResults.forEach((result) => actionResultIdsRef.current.add(normalizeIdentity(result.deviceId)));
        setReadyDevices((prev) => prev.map((device) => {
          if (!matchedResults.some((result) => normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId))) {
            return device;
          }

          const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
          return updatedDevice || device;
        }));
        setResults((prevResults) => {
          const nextResults = [
            ...prevResults.filter((result) => (
              !matchedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(result.deviceId))
            )),
            ...matchedResults,
          ];
          const finalResults = isLastResponse
            ? handleSurveillanceUnexpectedResponse({ socketIdType: activeRequest.type, results: nextResults, readyDevices })
            : nextResults;
          return finalResults;
        });
        applyAuthenticationResultsToDevices(matchedResults);

        const hasAllResults = actionResultIdsRef.current.size === readyDevices.length;
        if (isLastResponse || hasAllResults) {
          setAuthenticationLoading(false);
          setIsAuthenticationComplete(true);
          setAuthRequestSource(null);
          setSocketId(null);
          setSocketIdType(null);
          activeActionRequestRef.current = null;
          return;
        } else if (authRequestSource !== "slider") {
          setModalMode("auth");
        }

        continue;
      }

      const { updatedReadyDevices, updatedResults } = handleSurveillanceAdoptionResponse({ data, readyDevices });
      const matchedResults = updatedResults.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId)));

      matchedResults.forEach((result) => actionResultIdsRef.current.add(normalizeIdentity(result.deviceId)));
      setReadyDevices((prev) => prev.map((device) => {
        if (!matchedResults.some((result) => normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId))) {
          return device;
        }

        const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
        return updatedDevice || device;
      }));
      setDevices((prev) => prev.map((device) => {
        if (!matchedResults.some((result) => normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId))) {
          return device;
        }

        const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
        return updatedDevice ? { ...updatedDevice, checked: false } : device;
      }));
      setResults((prevResults) => {
        const nextResults = [
          ...prevResults.filter((result) => (
            !matchedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(result.deviceId))
          )),
          ...matchedResults,
        ];
        const finalResults = isLastResponse
          ? handleSurveillanceUnexpectedResponse({ socketIdType: activeRequest.type, results: nextResults, readyDevices })
          : nextResults;
        return finalResults;
      });

      const hasAllResults = actionResultIdsRef.current.size === readyDevices.length;
      if (isLastResponse || hasAllResults) {
        setAdoptionLoading(false);
        setIsAdoptionComplete(true);
        setSocketId(null);
        setSocketIdType(null);
        activeActionRequestRef.current = null;
        return;
      }
    }
  }, [wsData]);

  useEffect(() => {
    if (!socketId || !dataMap[socketId]) {
      return;
    }

    const response = dataMap[socketId];

    if (!checkMockup && [
      socketIdTypeOption.discovery,
      socketIdTypeOption.auth,
      socketIdTypeOption.adopt,
    ].includes(socketIdType)) {
      return;
    }

    if (response?.error || response?.response?.error) {
      setScanning(false);
      setScanningDone(true);
      setErrorMessage(getErrorMessage(response));
      setModalMode("error");
      return;
    }

    if (socketIdType === socketIdTypeOption.discovery) {
      if (scanningAbortRef.current) {
        return;
      }

      const data = getResponseData(response);
      if (data.length === 0) {
        setScanning(false);
        setScanningDone(true);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        return;
      }

      if (checkMockup) {
        data.forEach((item, index) => {
          setTimeout(() => {
            if (scanningAbortRef.current) {
              return;
            }

            setDevices((prev) => {
              const updatedDevices = handleSurveillanceDiscoveryResponse({
                data: [item],
                devices: prev,
                existedDevices,
                inventoryDevices,
              });

              return [...prev, ...updatedDevices];
            });

            if (index === data.length - 1) {
              setScanning(false);
              setScanningDone(true);

              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
            }
          }, mockBatchResponseInterval * (index + 1));
        });

        return;
      }

      setDevices((prev) => {
        const updatedDevices = handleSurveillanceDiscoveryResponse({
          data,
          devices: prev,
          existedDevices,
          inventoryDevices,
        });

        return [...prev, ...updatedDevices];
      });
      return;
    }

    if (socketIdType === socketIdTypeOption.auth) {
      const data = getResponseData(response);
      const isLastResponse = data.length === 0;
      const readyDeviceIds = new Set(readyDevices.map((device) => normalizeIdentity(device.deviceId)));

      if (checkMockup && data.length > 0) {
        const matchedData = data.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId || result.macAddress)));

        if (matchedData.length === 0) {
          const nextResults = handleSurveillanceUnexpectedResponse({
            socketIdType,
            results: results,
            readyDevices,
          });

          setResults(nextResults);
          applyAuthenticationResultsToDevices(nextResults);
          setAuthenticationLoading(false);
          setIsAuthenticationComplete(true);
          setAuthRequestSource(null);
          setSocketId(null);
          setSocketIdType(null);
          return;
        }

        matchedData.forEach((result, index) => {
          setTimeout(() => {
            const { updatedReadyDevices, updatedResults } = handleSurveillanceAuthenticationResponse({
              data: [result],
              readyDevices,
              username: credential.username,
              password: credential.password,
            });

            setReadyDevices((prev) => prev.map((device) => {
              const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
              return updatedDevice || device;
            }));
            setResults((prevResults) => [
              ...prevResults.filter((item) => (
                !updatedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(item.deviceId))
              )),
              ...updatedResults,
            ]);
            applyAuthenticationResultsToDevices(updatedResults);

            if (index === matchedData.length - 1 && matchedData.length === readyDevices.length) {
              setAuthenticationLoading(false);
              setIsAuthenticationComplete(true);
              setAuthRequestSource(null);
              setSocketId(null);
              setSocketIdType(null);
            }
          }, mockBatchResponseInterval * (index + 1));
        });

        return;
      }

      const { updatedReadyDevices, updatedResults } = handleSurveillanceAuthenticationResponse({
        data,
        readyDevices,
        username: credential.username,
        password: credential.password,
      });
      const matchedResults = updatedResults.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId)));

      setReadyDevices(updatedReadyDevices);
      setResults((prevResults) => {
        const nextResults = [
          ...prevResults.filter((result) => (
            !matchedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(result.deviceId))
          )),
          ...matchedResults,
        ];
        const finalResults = isLastResponse
          ? handleSurveillanceUnexpectedResponse({
            socketIdType,
            results: nextResults,
            readyDevices,
          })
          : nextResults;
        const hasAllResults = readyDevices.length > 0 && readyDevices.every((device) => (
          finalResults.some((result) => normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId))
        ));

        if (isLastResponse || hasAllResults) {
          setAuthenticationLoading(false);
          setIsAuthenticationComplete(true);
          setSocketId(null);
          setSocketIdType(null);
        }

        return finalResults;
      });
      applyAuthenticationResultsToDevices(matchedResults);

      if (authRequestSource === "slider") {
        const hasCurrentDeviceResult = matchedResults.some((result) => (
          normalizeIdentity(result.deviceId) === normalizeIdentity(selectedDevice?.deviceId)
        ));

        if (!isLastResponse && !hasCurrentDeviceResult) {
          return;
        }

        setAuthRequestSource(null);
        return;
      }

      setModalMode("auth");
      return;
    }

    if (socketIdType === socketIdTypeOption.adopt) {
      const data = getResponseData(response);
      const isLastResponse = data.length === 0;
      const readyDeviceIds = new Set(readyDevices.map((device) => normalizeIdentity(device.deviceId)));

      if (checkMockup && data.length > 0) {
        const matchedData = data.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId || result.macAddress)));

        if (matchedData.length === 0) {
          setResults((prevResults) => handleSurveillanceUnexpectedResponse({
            socketIdType,
            results: prevResults,
            readyDevices,
          }));
          setAdoptionLoading(false);
          setIsAdoptionComplete(true);
          setSocketId(null);
          setSocketIdType(null);
          return;
        }

        matchedData.forEach((result, index) => {
          setTimeout(() => {
            const { updatedReadyDevices, updatedResults } = handleSurveillanceAdoptionResponse({
              data: [result],
              readyDevices,
            });
            const matchedResults = updatedResults.filter((item) => readyDeviceIds.has(normalizeIdentity(item.deviceId)));

            setReadyDevices((prev) => prev.map((device) => {
              const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
              return updatedDevice || device;
            }));
            setResults((prevResults) => [
              ...prevResults.filter((item) => (
                !matchedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(item.deviceId))
              )),
              ...matchedResults,
            ]);
            setDevices((prev) => prev.map((device) => {
              const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
              return updatedDevice ? { ...updatedDevice, checked: false } : device;
            }));

            if (index === matchedData.length - 1 && matchedData.length === readyDevices.length) {
              setAdoptionLoading(false);
              setIsAdoptionComplete(true);
              setSocketId(null);
              setSocketIdType(null);
            }
          }, mockBatchResponseInterval * (index + 1));
        });

        return;
      }

      const { updatedReadyDevices, updatedResults } = handleSurveillanceAdoptionResponse({
        data,
        readyDevices,
      });
      const matchedResults = updatedResults.filter((result) => readyDeviceIds.has(normalizeIdentity(result.deviceId)));

      setReadyDevices(updatedReadyDevices);
      setResults((prevResults) => {
        const nextResults = [
          ...prevResults.filter((result) => (
            !matchedResults.some((updatedResult) => normalizeIdentity(updatedResult.deviceId) === normalizeIdentity(result.deviceId))
          )),
          ...matchedResults,
        ];
        const finalResults = isLastResponse
          ? handleSurveillanceUnexpectedResponse({
            socketIdType,
            results: nextResults,
            readyDevices,
          })
          : nextResults;
        const hasAllResults = readyDevices.length > 0 && readyDevices.every((device) => (
          finalResults.some((result) => (
            normalizeIdentity(result.deviceId) === normalizeIdentity(device.deviceId) &&
            Object.prototype.hasOwnProperty.call(result, "isAdopted")
          ))
        ));

        if (isLastResponse || hasAllResults) {
          setAdoptionLoading(false);
          setIsAdoptionComplete(true);
          setSocketId(null);
          setSocketIdType(null);
        }

        return finalResults;
      });
      setDevices((prev) => prev.map((device) => {
        const updatedDevice = updatedReadyDevices.find((item) => normalizeIdentity(item.deviceId) === normalizeIdentity(device.deviceId));
        return updatedDevice ? { ...updatedDevice, checked: false } : device;
      }));
    }
  }, [dataMap, socketId, socketIdType]);

  useEffect(() => {
    if (!socketId || ![socketIdTypeOption.auth, socketIdTypeOption.adopt].includes(socketIdType)) {
      return undefined;
    }

    if (socketIdType === socketIdTypeOption.auth && isAuthenticationComplete) {
      return undefined;
    }

    if (socketIdType === socketIdTypeOption.adopt && isAdoptionComplete) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const nextResults = handleSurveillanceUnexpectedResponse({
        socketIdType,
        results,
        readyDevices,
      });

      setResults(nextResults);
      if (socketIdType === socketIdTypeOption.auth) {
        applyAuthenticationResultsToDevices(nextResults);
        setAuthenticationLoading(false);
        setIsAuthenticationComplete(true);
        setAuthRequestSource(null);
      }

      if (socketIdType === socketIdTypeOption.adopt) {
        setAdoptionLoading(false);
        setIsAdoptionComplete(true);
      }

      setSocketId(null);
      setSocketIdType(null);
      activeActionRequestRef.current = null;
    }, scanningTimeout);

    return () => clearTimeout(timer);
  }, [authRequestSource, isAdoptionComplete, isAuthenticationComplete, readyDevices, socketId, socketIdType]);

  if (!hasAcknowledgedDiscoveryNotice || initializing) {
    return (
      !hasAcknowledgedDiscoveryNotice ? (
        <ModalContainer
          display
          modalWidth="750px"
          children={
            <ModalDiscoveryNotice
              onCloseModal={goToThePreviousTab}
              onNext={startDiscoverySetup}
            />
          }
        />
      ) : (
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "150px",
          }}
        >
          <Loading />
        </Box>
      )
    );
  }

  return (
    <>
      <Box>
        {hasSearched && (
          <Box className={mainStyle["tools-container"]}>
            <Box className={mainStyle["scan-status"]}>
              {!scanningDone && (
                <div className={`${mainStyle.progress} ${mainStyle.processing}`}>
                  <img src={icon_search_state} alt="" />
                  Scanning...
                </div>
              )}
              {scanningDone && !scanningStopped && (
                <div className={`${mainStyle.progress} ${mainStyle.done}`}>
                  <img src={icon_success_state} alt="" />
                  Scan Completed
                </div>
              )}
              {scanningDone && scanningStopped && (
                <div className={`${mainStyle.progress} ${mainStyle.stopped}`}>
                  <img src={icon_stopped_state} alt="" />
                  Scan Stopped
                </div>
              )}
            </Box>
            {scanningDone ? (
              <Box onClick={rediscover} className={mainStyle["scan-btn"]}>
                <img src={icon_start} alt="" />
                <span>Start</span>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" onClick={stopScanningDevices} className={mainStyle["scan-btn"]}>
                <img src={icon_stop} alt="" />
                <span>Stop</span>
              </Box>
            )}
            {scanningDone ? (
              <Box onClick={() => setShowDiscoverySettings(true)} className={mainStyle["scan-btn"]}>
                <img src={icon_setting} alt="" />
                <span>Discovery Settings</span>
              </Box>
            ) : (
              <Box className={`${mainStyle["scan-btn"]} ${mainStyle["scan-btn-disabled"]}`}>
                <img src={icon_setting_disabled} alt="" />
                <span>Discovery Settings</span>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <TableField
        {...tableInstance}
        pendingChildren={
          <>
            {!hasSearched && (
              <Box style={{ flex: "1" }}>
                <Box className={mainStyle["no-data"]}>
                  No Entity in the table
                </Box>
                <Box className={mainStyle["start-discovery"]}>
                  <Box
                    className={mainStyle.btn}
                    onClick={() => {
                      scanningAbortRef.current = false;
                      setScanningDone(false);
                      setHasSearched(true);
                      setDevices([]);
                      scanDevices();
                    }}
                  >
                    Start Discovery
                  </Box>
                </Box>
                <Box className={mainStyle.settings}>
                  <Box onClick={() => setShowDiscoverySettings(true)}>
                    Settings
                  </Box>
                </Box>
              </Box>
            )}
            {hasSearched && !scanning && devices.length === 0 && (
              <Box className={mainStyle["no-data"]} style={{ flex: "1" }}>
                No Entity in the table
              </Box>
            )}
            {scanning && <Loading />}
          </>
        }
        currentRowDataIndex={devices.findIndex((device) => device.deviceId === selectedDevice?.deviceId)}
        handleRowOnClick={(row) => {
          const device = devices.find((item) => item.deviceId === row.deviceId);

          if (!device?.authStatus) {
            return;
          }

          setSelectedDevice(device);
          setSliderAuthenticationErrorDeviceId(null);
          setOpenSlider(true);
        }}
      />

      <ModalDiscoverySettings
        display={showDiscoverySettings}
        loading={initializing}
        proxyDevices={proxyDevices}
        selectedProxyDevice={selectedProxyDevice}
        onSelectProxyDevice={(proxyDevice) => {
          setSelectedProxyDevice(proxyDevice);
          setSelectedProxyUuid(proxyDevice?.proxyUuid || "");
        }}
        onCancel={() => {
          setShowDiscoverySettings(false);
          if (!selectedProxyDevice) {
            goToThePreviousTab();
          }
        }}
        onSave={() => setShowDiscoverySettings(false)}
      />

      <Slider
        openSlider={openSlider}
        onToggleSlider={() => {
          setOpenSlider(false);
          setSliderAuthenticationErrorDeviceId(null);
        }}
        device={selectedDevice}
        authenticateDevice={authenticateDeviceFromSlider}
        authenticatingDeviceId={sliderAuthenticatingDeviceId}
        showAuthenticationError={showSliderAuthenticationError}
        openAdoption={openAdoption}
      />

      <ModalAuthentication
        display={modalMode === "auth"}
        currentStep={authenticationStep}
        devices={readyDevices}
        results={results}
        loading={authenticationLoading}
        canClose={isAuthenticationComplete}
        credential={credential}
        setCredential={setCredential}
        onCancel={() => {
          setModalMode(null);
          setAuthenticationStep(0);
          setAuthenticationLoading(false);
          setIsAuthenticationComplete(false);
        }}
        onNext={sendAuthenticationRequest}
        onClose={closeResultModal}
      />

      <ModalAdoption
        display={modalMode === "adopt"}
        currentStep={adoptionStep}
        scopeLevel={auth.scopeLevel}
        sites={sites}
        devices={readyDevices}
        results={results}
        loading={adoptionLoading}
        canClose={isAdoptionComplete}
        selectedSiteId={selectedSiteId}
        setSelectedSiteId={setSelectedSiteId}
        onCancel={() => {
          setModalMode(null);
          setAdoptionStep(0);
          setAdoptionLoading(false);
          setIsAdoptionComplete(false);
        }}
        onAdopt={sendAdoptionRequest}
        onClose={closeResultModal}
      />

      <ModalContainer display={modalMode === "error"} modalWidth="500px">
        <h2 className={mainStyle.modal_title}>Error</h2>
        <Typography>{errorMessage}</Typography>
        <Box className={mainStyle.modal_actions}>
          <OutlineButton width="90px" height="32px" onClick={() => setModalMode(null)}>
            Close
          </OutlineButton>
          <SolidButton width="90px" height="32px" onClick={retryScanningDevices}>
            Retry
          </SolidButton>
        </Box>
      </ModalContainer>
    </>
  );
};

export default Discover;
